import collect from 'collect.js';
import Query from '../query-builder';
import { ScreenEnum } from '../../hooks/navigation/Screens';

/**
 * Pure request-payload / query builders extracted from the Manager classes.
 *
 * Managers stay thin (axios + Redux dispatch); the data-shaping logic lives here
 * so it can be unit-tested without mocking the network — see
 * tests/services/api/transforms.test.js.
 */

/**
 * Build the update payload that toggles a product in/out of the "favourites"
 * category. Adds the favourites category id when absent, removes it when present,
 * and normalizes image / modifier_groups to the shapes the API expects.
 */
export function buildFavouriteTogglePayload(product, favourites) {
  const categories = collect(product.categories).pluck('id');

  return {
    ...product,
    image: product.image_url,
    modifier_groups: collect(product.modifier_groups).pluck('id').toArray(),
    categories: categories.contains(favourites?.id)
      ? categories.reject((item) => item === favourites?.id).toArray()
      : categories.push(favourites.id).toArray(),
    _method: 'PUT',
  };
}

/** Build the companies list-endpoint URL from optional filters/pagination. */
export function buildCompaniesQuery(data = {}, sort = '-companies.id') {
  return new Query()
    .for('companies')
    .where('companies.name', data.name ?? null)
    .where('franchise_id', data.franchise_id ?? null)
    .whereIn('except', data.except ?? [])
    .when(data.id, (builder) => builder.where('id', data.id))
    .page(data.page ?? 1)
    .sort(sort)
    .get();
}

/** Build the delivery-schedule PUT payload; presence flag derives from the list. */
export function buildDeliverySchedulePayload(data) {
  return {
    has_delivery_schedule: data.length > 0,
    delivery_schedule: data,
  };
}

/**
 * Build the business-documents index query params.
 *
 * The backend's GetDocumentsRequest declares `with_trashed` *prohibited* unless the
 * caller holds `business_profiles::manage_docs` (ManageBusinessProfilesDocs), so sending
 * it for a user without that permission 422s the whole list request and the Documents
 * page renders empty. Resellers, sales and onboarding managers and store admins reach the
 * page with the read-only `business_profiles::read_docs` (ReadBusinessProfilesDocs), not the manage one,
 * so they would hit exactly that 422. Only request trashed documents when the user is
 * allowed to view them.
 */
export function buildDocumentsQueryParams({ canViewTrashed, filter }) {
  const params = { filter };

  if (canViewTrashed) {
    params.with_trashed = true;
  }

  return params;
}

/** Map initial-data navigation_stats to the sidebar badge counts, keyed by screen. */
export function buildNavigationBadges(data) {
  return {
    [ScreenEnum.BillingPayouts]: data?.navigation_stats?.pending_payouts,
    [ScreenEnum.PaymentGateways]: data?.navigation_stats?.pending_payment_gateway_accounts,
    [ScreenEnum.SetupPackageFulfillments]: data?.navigation_stats?.pending_setup_package_fulfillments,
    [ScreenEnum.DataDeletionRequests]: data?.navigation_stats?.pending_user_deletion_requests,
  };
}

/**
 * Query for `GET /franchises/slug-available`. Slugs are unique per shop root, so the
 * backend checks against `shop_domain_id` when given — the root a form picked — else the
 * franchise's own root when `franchise_id` is given, else the provider's primary root;
 * `franchise_id` also keeps a franchise's own slug from counting against it. Only the
 * ids a caller has are sent: the onboarding wizard asks with the slug alone.
 */
export function buildSlugAvailabilityParams(slug, { shopDomainId = null, franchiseId = null } = {}) {
  return {
    slug,
    ...(shopDomainId !== null ? { shop_domain_id: shopDomainId } : {}),
    ...(franchiseId !== null ? { franchise_id: franchiseId } : {}),
  };
}
